import React, { useState, useRef, useEffect } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const EMMY_AVATAR = "https://static.prod-images.emergentagent.com/jobs/cd6603fe-63bb-4b5e-b7b1-3d91ebe64790/images/a4abb950bd1446f223c20b7c9a146a761224c9ff86e2e4e50a4e0a2dfe441725.jpeg";

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // While the member is scrolling, Emmy slides away and stops intercepting taps so she
  // never blocks a button underneath; she returns a moment after scrolling stops.
  const [scrolling, setScrolling] = useState(false);
  // Swipe-to-dismiss: the member can drag Emmy to the right to tuck her away when they
  // don't need her; she's replaced by a slim edge handle they can tap to bring her back.
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("emmy_dismissed") === "1"; } catch { return false; }
  });
  const [dragX, setDragX] = useState(0);
  const drag = useRef({ active: false, startX: 0, startY: 0, moved: false });
  const scrollRef = useRef(null);

  const dismiss = () => { setDismissed(true); setDragX(0); try { localStorage.setItem("emmy_dismissed", "1"); } catch { /* ignore */ } };
  const restore = () => { setDismissed(false); setDragX(0); try { localStorage.removeItem("emmy_dismissed"); } catch { /* ignore */ } };

  const onPointerDown = (e) => {
    drag.current = { active: true, startX: e.clientX, startY: e.clientY, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) drag.current.moved = true;
    setDragX(Math.max(0, dx)); // only allow dragging toward the right edge
  };
  const onPointerUp = () => {
    if (!drag.current.active) return;
    const moved = drag.current.moved;
    const far = dragX > 56;
    drag.current.active = false;
    if (far) dismiss();
    else { setDragX(0); if (!moved) setOpen(true); } // a tap (no drag) opens the chat
  };

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, busy]);

  useEffect(() => {
    let t;
    const onScroll = () => {
      setScrolling(true);
      clearTimeout(t);
      t = setTimeout(() => setScrolling(false), 700);
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => { window.removeEventListener("scroll", onScroll, { capture: true }); clearTimeout(t); };
  }, []);

  const pageContext = () => {
    const h1 = document.querySelector("h1")?.textContent?.trim();
    const title = document.title;
    const path = window.location.pathname;
    return [title && title !== "Two Degrees" ? title : null, h1, `page: ${path}`].filter(Boolean).join(" — ").slice(0, 400);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await api("/ai/chat", { method: "POST", body: { message: text, session_id: sessionId, context: pageContext() } });
      if (r.session_id) setSessionId(r.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: r.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: e.message || "Emmy is unavailable right now." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="ai-assistant-fab"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { drag.current.active = false; setDragX(0); }}
        aria-label="Chat with Emmy — drag right to dismiss"
        style={{ transform: dragX ? `translateX(${dragX}px)` : undefined, touchAction: "none" }}
        className={`fixed bottom-20 lg:bottom-6 right-4 z-40 w-14 h-14 rounded-full bg-surface shadow-lg shadow-brand/30 ring-2 ring-brand/70 overflow-hidden flex items-center justify-center hover:scale-105 active:scale-95 ${dragX ? "" : "transition-all duration-200"} ${dismissed || scrolling || open ? "opacity-0 translate-y-24 pointer-events-none" : "opacity-100"}`}
        title="Chat with Emmy (drag right to hide)"
      >
        <img src={EMMY_AVATAR} alt="Emmy" draggable={false} className="w-full h-full object-cover pointer-events-none" />
      </button>

      {dismissed && !open && (
        <button
          data-testid="ai-assistant-restore"
          onClick={restore}
          aria-label="Bring Emmy back"
          className="fixed bottom-24 lg:bottom-10 right-0 z-40 w-7 h-14 rounded-l-xl bg-surface shadow-lg shadow-brand/30 border-2 border-r-0 border-brand/70 overflow-hidden flex items-center justify-center hover:w-9 transition-all duration-200"
          title="Bring Emmy back"
        >
          <img src={EMMY_AVATAR} alt="Emmy" className="w-10 h-10 rounded-full object-cover opacity-90 -mr-3" />
        </button>
      )}

      {open && (
        <div data-testid="ai-assistant-panel" className="fixed inset-0 z-[90] flex justify-end" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-ink/30" />
          <div className="relative w-full max-w-md bg-surface h-full flex flex-col shadow-2xl animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <div className="flex items-center gap-2.5">
                <img src={EMMY_AVATAR} alt="Emmy" className="w-9 h-9 rounded-full object-cover ring-2 ring-brand/30" />
                <div>
                  <p className="font-serif text-lg text-ink leading-tight">Emmy</p>
                  <p className="text-xs text-muted-foreground">Your Two Degrees assistant</p>
                </div>
              </div>
              <button data-testid="ai-assistant-close" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-ink"><X className="w-5 h-5" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
              {msgs.length === 0 && (
                <div className="text-center text-muted-foreground text-sm mt-10" data-testid="ai-empty">
                  <img src={EMMY_AVATAR} alt="Emmy" className="w-16 h-16 rounded-full object-cover mx-auto mb-3 ring-2 ring-brand/30" />
                  <p className="text-ink font-medium mb-1">Hi, I&apos;m Emmy 👋</p>
                  <p>Ask me to draft a message, brainstorm outreach, polish your bio, or explain anything in the app.</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {["Write a networking intro", "Ideas to grow my network", "Draft a follow-up message"].map((q) => (
                      <button key={q} onClick={() => setInput(q)} className="px-3 py-1.5 rounded-full border border-line text-xs text-ink hover:border-brand hover:text-brand transition-colors">{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} data-testid={`ai-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "self-end bg-brand text-white" : "self-start bg-surface-2 text-ink"}`}>
                  {m.text}
                </div>
              ))}
              {busy && <div className="self-start bg-surface-2 rounded-2xl px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
            </div>

            <div className="px-4 py-3 border-t border-line flex items-end gap-2">
              <textarea
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask Emmy anything…"
                rows={1}
                className="flex-1 resize-none bg-surface-2 border border-line rounded-2xl px-4 py-2.5 text-sm text-ink outline-none focus:border-brand max-h-32"
              />
              <button data-testid="ai-send" onClick={send} disabled={busy || !input.trim()} className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center disabled:opacity-40 shrink-0">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
